<template>
    <div class="ticket-column-card">
        <div class="column-content">
            <!-- Kolon Başlığı -->
            <div class="column-header" @mouseover="showSelectAll = true" @mouseleave="showSelectAll = false">
                <h3>{{ group.title }} ({{ group.tickets.length }})</h3>
                <!-- Tümünü Seç Checkbox'ı -->
                <div v-if="showSelectAll" class="select-all-checkbox">
                    <input type="checkbox" :checked="isAllSelected" @change="toggleSelectAll" />
                </div>
            </div>
            <!-- Bilet Listesi -->
            <draggable
                class="tickets-list"
                :options="ticketDragOptions"
                :group="{ name: 'tickets', pull: true, put: true }"
                @add="onAdd"
                v-model="group.tickets"
                :disabled="isSelectionMode"
            >
                <transition-group tag="div" class="ticket-drag" name="list">
                    <div
                        v-for="ticket in group.tickets"
                        :key="ticket._id"
                        class="ticket-item"
                        :data-id="ticket._id"
                        :data-status="ticket.status"
                        :class="{ 'selection-mode': isSelectionMode }"
                        @click="handleTicketClick(ticket._id)"
                    >
                        <!-- Kart Seçim Checkbox'ı -->
                        <div class="ticket-select-checkbox" @click.stop>
                            <input
                                type="checkbox"
                                :checked="isTicketSelected(ticket._id)"
                                @change="toggleTicketSelection(ticket._id)"
                            />
                        </div>
                        <div class="ticket-content">
                            <div class="ticket-details">
                                <span class="ticket-subject">{{ ticket.subject }}</span>
                                <span class="ticket-person">
                                    <i class="material-icons">person</i>
                                    {{ getVisitorValue(ticket, "name") }}</span
                                >
                                <span class="ticket-email">
                                    <i class="material-icons">email</i>
                                    {{ getVisitorValue(ticket, "email") }}</span
                                >
                                <span class="ticket-phone">
                                    <i class="material-icons">phone</i>
                                    {{ getVisitorValue(ticket, "phone") }}</span
                                >
                                <span class="ticket-type">{{ ticket.type }}</span>
                                <span class="ticket-assigned">
                                    <i class="material-icons">assignment_ind</i>
                                    Temsilci: {{ ticket.assignedUser ? ticket.assignedUser.name : "Atanmamış" }}
                                </span>
                                <span class="ticket-date">{{ formatDate(ticket.createdDate) }}</span>
                            </div>
                            <span class="ticket-status">{{ ticket.status }}</span>
                        </div>
                    </div>
                </transition-group>
            </draggable>
        </div>
    </div>
</template>

<script>
import Draggable from "vuedraggable";
import { maskContactInfo, isMaskContactInfo } from "../../../../utils/utils";

export default {
    name: "TicketColumnCard",
    components: {
        Draggable,
    },
    props: {
        group: {
            type: Object,
            required: true,
        },
        dndEnabled: {
            type: Boolean,
            default: true,
        },
        selectedTickets: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            ticketDragOptions: {
                animation: 200,
                ghostClass: "ghost",
                chosenClass: "chosen",
            },
            showSelectAll: false,
            isSelectionMode: false,
        };
    },
    watch: {
        selectedTickets: {
            handler(newValue) {
                this.isSelectionMode = newValue.length > 0;
            },
            immediate: true,
        },
    },
    computed: {
        isSelectionMode() {
            return this.selectedTickets.length > 0;
        },
    },
    methods: {
        onAdd(event) {
            const ticketId = event.item.getAttribute("data-id");
            const oldStatus = event.item.getAttribute("data-status");
            const newStatusTitle = this.group.title;

            if (oldStatus !== newStatusTitle) {
                this.$emit("update-ticket-status", { ticketId, statusTitle: newStatusTitle });
            }
        },
        toggleTicketSelection(ticketId) {
            this.$emit("toggle-ticket-selection", ticketId);
        },
        isTicketSelected(ticketId) {
            return this.selectedTickets.includes(ticketId);
        },
        toggleSelectAll() {
            this.$emit("toggle-select-all", this.group.tickets);
        },
        isAllSelected() {
            return this.group.tickets.every((ticket) => this.selectedTickets.includes(ticket._id));
        },
        formatDate(date) {
            const options = { year: "numeric", month: "short", day: "numeric" };
            return new Date(date).toLocaleDateString("tr-TR", options);
        },
        getVisitorValue(item, field) {
            if (field === "name") {
                return item.osrPayload?.name || item.visitor?.fullname || item.conversation?.fullname || "-";
            } else if (field === "email") {
                const val = item.osrPayload?.email || item.visitor?.email || item.conversation?.email || "-";
                return isMaskContactInfo('email') ? maskContactInfo(val) : val;
            } else if (field === "phone") {
                const val = item.osrPayload?.phone || item.visitor?.phone || item.visitor?.whatsappNumber || "-";
                return isMaskContactInfo('phone') ? maskContactInfo(val) : val;
            }
            let value = !!item.conversation
                ? item.conversation[field] || "-"
                : !!item.osrPayload
                ? item.osrPayload[field]
                : null;
            if (item.createdByChatbot) {
                if (field === "email") {
                    value = item.visitorEmailAddress || value;
                }
                if (field === "phone") {
                    value = item.visitorPhoneNumber || value;
                }
            }
            return value || item.visitor?.[field] || item[field] || "-";
        },
        handleTicketClick(ticketId) {
            if (this.isSelectionMode) {
                this.toggleTicketSelection(ticketId);
            } else {
                const ticket = this.group.tickets.find(t => t._id === ticketId);
                this.$emit('ticket-clicked', ticket);
            }
        },
    },
};
</script>

<style scoped lang="scss">
.ticket-column-card {
    background: linear-gradient(135deg, #f4f6f8, #e0e4ea);
    border-radius: 8px;
    width: 300px;
    height: calc(100vh - 250px);
    overflow: hidden;

    .column-content {
        display: flex;
        flex-direction: column;
        height: 100%;
    }

    .column-header {
        background-color: #ffffff;
        padding: 10px;
        border-radius: 4px 4px 0 0;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        background: linear-gradient(135deg, #ff9a9e, #fad0c4);
        padding-top: 20px;
        padding-bottom: 10px;
        position: relative;

        h3 {
            margin: 0;
            font-size: 16px;
            color: #333333;
            text-align: center;
        }

        .select-all-checkbox {
            position: absolute;
            top: 10px;
            right: 10px;
            cursor: pointer;

            input[type="checkbox"] {
                width: 18px;
                height: 18px;
            }
        }
    }

    .tickets-list {
        flex-grow: 1;
        overflow-y: auto;
        background: linear-gradient(to bottom, #d3d6dc, white);
        padding: 10px;
        border-radius: 0 0 4px 4px;
        min-height: 50px; // Boş kolonların da drop alanı olmasını sağlar
        .ticket-drag {
            width: 100%;
            height: 100%;
        }

        .ticket-item {
            background-color: #ffffff;
            border: 1px solid #e0e0e0;
            border-radius: 4px;
            padding: 10px;
            margin-bottom: 8px;
            cursor: pointer; // Tıklanabilir olduğunu belirtmek için imleç stilini değiştiriyoruz
            transition: background-color 0.3s;
            position: relative;

            &:hover {
                background-color: #f0f0f0;
            }

            .ticket-select-checkbox {
                position: absolute;
                top: 10px;
                left: 10px;
                cursor: pointer;
                opacity: 0;
                transition: opacity 0.3s ease;

                input[type="checkbox"] {
                    width: 18px;
                    height: 18px;
                }
            }

            &:hover .ticket-select-checkbox,
            .ticket-select-checkbox input:checked,
            &.selection-mode .ticket-select-checkbox {
                opacity: 1;
            }

            .ticket-content {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                padding-left: 35px; // Checkbox için boşluk bırakıyoruz

                .ticket-details {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;

                    span {
                        display: flex;
                        align-items: center;
                        gap: 4px;

                        .material-icons {
                            font-size: 14px;
                            color: #777777;
                        }
                    }

                    .ticket-subject {
                        font-weight: 600;
                        color: #555555;
                        font-size: 14px;
                    }

                    .ticket-subject,
                    .ticket-person,
                    .ticket-email,
                    .ticket-phone,
                    .ticket-type,
                    .ticket-assigned,
                    .ticket-date {
                        font-size: 12px;
                        color: #777777;
                    }

                    .ticket-person,
                    .ticket-assigned {
                        font-weight: 500;
                        color: #333333;
                    }
                }

                .ticket-status {
                    font-size: 12px;
                    padding: 2px 6px;
                    border-radius: 12px;
                    background-color: #d1e7dd;
                    color: #0f5132;
                }
            }
        }
    }

    .ghost {
        opacity: 0.4;
    }

    .chosen {
        border: 2px solid #1890ff;
    }
}

.list-move {
    transition: transform 0.5s;
}

.list-enter-active,
.list-leave-active {
    transition: all 0.5s;
}

.list-enter,
.list-leave-to {
    opacity: 0;
    transform: translateX(30px);
}
</style>
